// scheduling/components/pool/PoolJobCard.jsx
// One waiting job in a pool (Job Pools, Trello #257).
//
// Card anatomy follows the POC — client, type, niche, budget/PY hours, days in
// pool, due date, releasing manager, notes — but uses RPRS's own type colors and
// chip styling rather than the POC's inline palette.
//
// The rank badge matters: each pool is first-in, first-out WITHIN EACH DIFFICULTY
// (§2.3 as revised 2026-10-29), and the server enforces that a self-claim takes the
// oldest job of its difficulty. So `rank` is the card's place within its band, the
// oldest of each band is emphasized, and every other card's Claim button is disabled
// with an explanation, rather than letting someone click and get a 409 back.
//
// `priority` from the POC is deliberately not implemented — oldest first only.

import { useState } from 'react';
import { POOL_META, DIFFICULTY_META, difficultyBand, difficultyLabel } from '../../utils/poolEligibility';
import { JOB_TYPE_COLORS } from '../../utils/schedConstants';
import PoolNotesEditor from './PoolNotesEditor';

const PE_JOB_URL = (peJobIndex) => `https://bmss.pehosted.com/PE/Job/Job/${peJobIndex}?panel=0`;

function daysSince(iso) {
  if (!iso) return 0;
  const then = new Date(iso);
  return Math.max(0, Math.floor((Date.now() - then.getTime()) / 86400000));
}
function fmtDate(iso) {
  if (!iso) return '—';
  const s = typeof iso === 'string' ? iso.slice(0, 10) + 'T12:00:00' : iso;
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
const fmtHours = (h) => (h == null ? null : String(Math.round(Number(h) * 100) / 100));

export default function PoolJobCard({
  job, rank, canClaim, canAssign, canEditNotes, onNotesSaved, claimBlockedReason,
  onClaim, onAssign, busy,
}) {
  const [editingNotes, setEditingNotes] = useState(false);
  const waited = daysSince(job.PooledAt);
  const stale = waited >= 5;
  const isHead = rank === 1;
  const typeColor = JOB_TYPE_COLORS[job.ServiceDesc] || JOB_TYPE_COLORS[job.JobType] || { bg: '#E5E7EB', text: '#374151' };
  const accent = POOL_META[job.PoolCode]?.color || '#6B7280';
  const band = difficultyBand(job.Complexity);
  const difficulty = DIFFICULTY_META[band];   // undefined when PE has none set
  const rankTitle = band
    ? `#${rank} of the ${difficultyLabel(band)} jobs waiting — oldest first within each difficulty`
    : `#${rank} of the jobs with no difficulty set — oldest first within each difficulty`;

  return (
    <div className={`sched-pool-card${isHead ? ' sched-pool-card--head' : ''}`} style={{ borderLeftColor: accent }}>
      <div className={`sched-pool-card-rank${isHead ? ' sched-pool-card-rank--head' : ''}`} title={rankTitle}>{rank}</div>

      <div className="sched-pool-card-body">
        <div className="sched-pool-card-title">
          <span className="sched-pool-card-client">{job.ClientName}</span>
          <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
            {job.ServiceDesc || job.JobType || 'General'}
          </span>
          {/* PE difficulty. Hidden when PE has none — 25-60% of open poolable jobs,
              depending on service — rather than a "Not set" chip on most cards. */}
          {difficulty && (
            <span className="sched-pool-card-difficulty" style={{ background: difficulty.bg, color: difficulty.color }}
              title="Difficulty, from the job's complexity in Practice Engine">
              {difficulty.label}
            </span>
          )}
          {job.NicheName && <span className="sched-pool-card-niche">{job.NicheName}</span>}
          <span className="sched-pool-card-code">{job.JobCode}</span>
        </div>

        <div className="sched-pool-card-name">{job.JobName}</div>

        <div className="sched-pool-card-meta">
          <span>
            {fmtHours(job.BudgetHours) || 0}h budget
            {job.PyHours != null && <span className="sched-pool-card-py"> · {fmtHours(job.PyHours)}h PY</span>}
          </span>
          {/* Time waiting is the FIFO key made visible — it's why this card is where
              it is in the list. */}
          <span className={stale ? 'sched-pool-card-stale' : undefined}>
            In pool {waited === 0 ? 'today' : `${waited}d`}
            {stale ? ' ⚠' : ''}
          </span>
          <span>Due {fmtDate(job.Deadline || job.DueDate)}</span>
          {/* The JOB's manager — per the requirements doc, and it's who a claimer would
              ask about the work. Falls back to the partner when a job has no manager. */}
          {(job.ManagerName || job.PartnerName) && (
            <span title={job.ManagerName ? 'Job manager' : 'Job partner (no manager set)'}>
              {job.ManagerName ? 'Mgr' : 'Partner'} {job.ManagerName || job.PartnerName}
            </span>
          )}
          {/* Who released it, shown only when that ISN'T the manager — otherwise it's
              the same name twice. Worth surfacing when they differ: a partner or admin
              pooling someone else's job is exactly the case you'd want visible. */}
          {job.PooledByName && job.PooledByName !== job.ManagerName && (
            <span title="Released into the pool by">Pooled by {job.PooledByName}</span>
          )}
        </div>

        {/* Notes: read-only for claimers, editable in place for the job's leads and the
            person who pooled it (doc: "editable after the fact by the manager"). The
            "Add notes" affordance appears when there are none, since an empty note is
            the case most worth filling in. */}
        {editingNotes ? (
          <PoolNotesEditor
            poolJobId={job.PoolJobId}
            notes={job.Notes}
            onSaved={(next) => { setEditingNotes(false); onNotesSaved?.(job.PoolJobId, next); }}
            onCancel={() => setEditingNotes(false)}
          />
        ) : job.Notes ? (
          <div className="sched-pool-card-notes">
            &ldquo;{job.Notes}&rdquo;
            {canEditNotes && (
              <button type="button" className="sched-pool-notes-editlink"
                onClick={() => setEditingNotes(true)} title="Edit these notes">
                Edit
              </button>
            )}
          </div>
        ) : canEditNotes ? (
          <button type="button" className="sched-pool-notes-editlink sched-pool-notes-editlink--add"
            onClick={() => setEditingNotes(true)}>
            + Add notes
          </button>
        ) : null}

        {stale && (
          <div className="sched-pool-card-warn">
            Waiting {waited} days — this should be claimed before newer work.
          </div>
        )}
      </div>

      <div className="sched-pool-card-actions">
        {job.PEJobIndex && (
          <a
            className="sched-pool-card-pe"
            href={PE_JOB_URL(job.PEJobIndex)}
            target="_blank"
            rel="noopener noreferrer"
            title="Open this job in Practice Engine"
          >
            PE ↗
          </a>
        )}
        {canAssign && (
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-card-assign"
            onClick={() => onAssign(job)}
            disabled={busy}
          >
            Assign
          </button>
        )}
        {canClaim && (
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-card-claim"
            onClick={() => onClaim(job)}
            disabled={busy || !isHead}
            title={isHead ? 'Claim this job' : claimBlockedReason || 'Claim the oldest job of this difficulty first'}
          >
            Claim
          </button>
        )}
      </div>
    </div>
  );
}
